import { DesktopTopBar } from "./DesktopTopBar";
import { KpiStrip } from "./KpiStrip";
import { Timeline } from "../Timeline";
import { EccTimeline } from "../EccTimeline";
import { Upcoming } from "../Upcoming";
import { Inzet } from "../Inzet";
import { Section } from "../Section";
import { DocsHub } from "../docs/DocsHub";
import { StorageEstimate } from "../StorageEstimate";
import { useState } from "react";
import {
  ecc as eccItems,
  showDayInzet,
} from "../../lib/data";
import { DOCS } from "../../lib/docs";

export function DesktopShell({
  onLock,
  onOpenRapport,
  onOpenBommelding,
}: {
  onLock: () => void;
  onOpenRapport: () => void;
  onOpenBommelding: () => void;
}) {
  return (
    <div className="min-h-full">
      <DesktopTopBar onLock={onLock} />
      <div className="px-6 max-w-[1800px] mx-auto flex justify-end -mb-2 mt-2">
        <button
          onClick={onOpenBommelding}
          className="text-xs text-risk hover:text-red-300 border border-risk/40 rounded-lg px-3 py-1.5 mr-2 transition-colors"
        >
          Bommelding-protocol
        </button>
        <button
          onClick={onOpenRapport}
          className="text-xs text-zinc-400 hover:text-zinc-200 border border-line rounded-lg px-3 py-1.5 transition-colors"
        >
          Rapport — meldingen en instroom (afdrukbaar)
        </button>
      </div>
      <main
        id="main"
        className="px-6 py-4 max-w-[1800px] mx-auto pb-12"
      >
        <h1 className="sr-only">Pal Mundo Control Panel — desktop weergave</h1>
        <KpiStrip />
        <div className="grid grid-cols-12 gap-4 mt-4">
          <section
            aria-labelledby="col-tijdlijn"
            className="col-span-5 xl:col-span-4"
          >
            <ColumnHeader
              id="col-tijdlijn"
              title="Tijdlijn"
              subtitle="Chronologische feed"
            />
            <div className="card p-3 max-h-[calc(100vh-260px)] overflow-y-auto">
              <Timeline onOpenBommelding={onOpenBommelding} />
            </div>
          </section>

          <section
            aria-labelledby="col-ecc"
            className="col-span-4 xl:col-span-4 space-y-4"
          >
            <div>
              <ColumnHeader
                id="col-ecc"
                title="ECC checkpoints"
                subtitle={`${eccItems.length} overleggen`}
                accent="warn"
              />
              <div className="card p-3">
                <EccTimeline />
              </div>
            </div>
            <div>
              <ColumnHeader
                title="Komende 90 min"
                subtitle="acts met aandacht licht op"
              />
              <div className="card p-3">
                <Upcoming />
              </div>
            </div>
          </section>

          <section
            aria-labelledby="col-side"
            className="col-span-3 xl:col-span-4"
          >
            <RightPanel labelId="col-side" />
          </section>
        </div>
        <div className="mt-6 px-1 flex items-center justify-end gap-3 opacity-70">
          <StorageEstimate />
        </div>
      </main>
    </div>
  );
}

function ColumnHeader({
  title,
  subtitle,
  accent,
  id,
}: {
  title: string;
  subtitle?: string;
  accent?: "default" | "warn" | "risk" | "ok";
  id?: string;
}) {
  const dot =
    accent === "warn"
      ? "bg-warn"
      : accent === "risk"
        ? "bg-risk"
        : accent === "ok"
          ? "bg-ok"
          : "bg-accent";
  return (
    <div className="flex items-center gap-2 mb-2 px-1">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <h2
        id={id}
        className="text-2xs tracking-eyebrow uppercase font-semibold text-zinc-300"
      >
        {title}
      </h2>
      {subtitle && (
        <span className="text-2xs text-zinc-500">· {subtitle}</span>
      )}
    </div>
  );
}

function RightPanel({ labelId }: { labelId: string }) {
  const [tab, setTab] = useState<"docs" | "inzet">("docs");
  return (
    <>
      <h2 id={labelId} className="sr-only">
        Bronnen en inzet
      </h2>
      <div role="tablist" className="flex items-center gap-1 mb-2 px-1">
        <button
          role="tab"
          aria-selected={tab === "docs"}
          onClick={() => setTab("docs")}
          className={`px-2.5 py-1.5 rounded-md text-2xs uppercase tracking-eyebrow font-semibold transition-colors ${
            tab === "docs"
              ? "bg-accent/15 text-accent"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Bronnen
          <span className="opacity-60 ml-1.5 tabular">{DOCS.length}</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === "inzet"}
          onClick={() => setTab("inzet")}
          className={`px-2.5 py-1.5 rounded-md text-2xs uppercase tracking-eyebrow font-semibold transition-colors ${
            tab === "inzet"
              ? "bg-accent/15 text-accent"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Inzet
          <span className="opacity-60 ml-1.5 tabular">
            {showDayInzet.length}
          </span>
        </button>
      </div>
      <div
        role="tabpanel"
        className="card p-3 max-h-[calc(100vh-260px)] overflow-y-auto"
      >
        {tab === "docs" ? (
          <DocsHub />
        ) : (
          // TODO(scott-call): datum toevoegen aan sectietitel, bijv. "Inzet beveiliging — 12 sep"
          <Section
            title="Inzet beveiliging"
            count={`${showDayInzet.length} posities`}
          >
            <Inzet />
          </Section>
        )}
      </div>
    </>
  );
}
